import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './CarGame.css';

const LANE_X = { '-1': 28, '0': 50, '1': 72 };
const OBSTACLE_CARS = ['🚗', '🚙', '🚕', '🚌', '🏎️', '🚐', '🚓', '🚑', '🚒'];
const COIN = '⭐';
const TREE_L = ['🌲', '🌳', '🌴'];
const TREE_R = ['🏠', '⛽', '🏢', '🏪', '🌲'];

function CarGame() {
  const navigate = useNavigate();
  const [lane, setLane] = useState(0);
  const [obstacles, setObstacles] = useState([]);
  const [coins, setCoins] = useState([]);
  const [scenery, setScenery] = useState([]);
  const [score, setScore] = useState(0);
  const [coinCount, setCoinCount] = useState(0);
  const [speed, setSpeed] = useState(2.5);
  const [distance, setDistance] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [started, setStarted] = useState(false);
  const [flash, setFlash] = useState('');
  const [tilt, setTilt] = useState(0);
  const [shakeRoad, setShakeRoad] = useState(false);
  const [highScore] = useState(() => {
    try { return parseInt(localStorage.getItem('demohub_car_best') || '0', 10); } catch { return 0; }
  });
  const [best, setBest] = useState(highScore);

  const refs = useRef({ lane: 0, score: 0, speed: 2.5, over: false, frame: 0 });
  const rafRef = useRef(null);
  const touchRef = useRef(null);

  const sync = () => {
    refs.current.lane = lane;
    refs.current.score = score;
    refs.current.speed = speed;
    refs.current.over = gameOver;
  };
  useEffect(sync);

  const move = useCallback((dir) => {
    if (refs.current.over) return;
    if (!started) setStarted(true);
    setLane((p) => {
      const n = p + dir;
      if (n < -1 || n > 1) return p;
      refs.current.lane = n;
      setTilt(dir * 18);
      setTimeout(() => setTilt(0), 180);
      return n;
    });
  }, [started]);

  // Keyboard
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); move(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); move(1); }
      if (e.key === ' ' && gameOver) { e.preventDefault(); restart(); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [move, gameOver]); // eslint-disable-line react-hooks/exhaustive-deps

  // Game loop
  useEffect(() => {
    if (!started || gameOver) return;
    const tick = () => {
      refs.current.frame++;
      const f = refs.current.frame;
      const spd = refs.current.speed;

      // Move obstacles
      setObstacles((prev) => {
        let arr = prev.map((o) => ({ ...o, z: o.z - spd * 0.6 })).filter((o) => o.z > -15);
        // Collision — only when obstacle is very close to player (z 0-5)
        for (const o of arr) {
          if (o.z < 5 && o.z > -2 && o.lane === refs.current.lane) {
            setGameOver(true);
            refs.current.over = true;
            setShakeRoad(true);
            setTimeout(() => setShakeRoad(false), 500);
            const fs = refs.current.score;
            if (fs > best) {
              setBest(fs);
              try { localStorage.setItem('demohub_car_best', fs.toString()); } catch {}
            }
            return arr;
          }
        }
        // Spawn — further away so player sees them coming
        if (f % Math.max(30, 60 - Math.floor(spd * 3)) === 0) {
          const l = [-1, 0, 1][Math.floor(Math.random() * 3)];
          const last = arr[arr.length - 1];
          if (!last || last.z < 60) {
            arr.push({ id: f + Math.random(), lane: l, z: 140, emoji: OBSTACLE_CARS[Math.floor(Math.random() * OBSTACLE_CARS.length)] });
          }
        }
        return arr;
      });

      // Coins
      setCoins((prev) => {
        let arr = prev.map((c) => ({ ...c, z: c.z - spd * 0.6 })).filter((c) => c.z > -15);
        // Collect
        const collected = arr.filter((c) => c.z < 5 && c.z > -2 && c.lane === refs.current.lane);
        if (collected.length > 0) {
          arr = arr.filter((c) => !collected.includes(c));
          setCoinCount((p) => p + collected.length);
          setScore((p) => { const ns = p + 50 * collected.length; refs.current.score = ns; return ns; });
          setFlash('coin');
          setTimeout(() => setFlash(''), 300);
        }
        // Spawn
        if (f % 80 === 0 && Math.random() > 0.4) {
          const l = [-1, 0, 1][Math.floor(Math.random() * 3)];
          arr.push({ id: f + Math.random() + 0.5, lane: l, z: 150 });
        }
        return arr;
      });

      // Scenery
      setScenery((prev) => {
        let arr = prev.map((s) => ({ ...s, z: s.z - spd * 0.5 })).filter((s) => s.z > -15);
        if (f % 35 === 0) {
          arr.push({
            id: f + Math.random() + 0.1,
            side: Math.random() > 0.5 ? 1 : -1,
            z: 140,
            emoji: (Math.random() > 0.5 ? TREE_L : TREE_R)[Math.floor(Math.random() * 3)],
          });
        }
        return arr;
      });

      // Score + distance
      if (f % 2 === 0) {
        setScore((s) => { const ns = s + 1; refs.current.score = ns; return ns; });
        setDistance((d) => d + 1);
      }

      // Speed up
      if (f % 400 === 0 && spd < 10) {
        setSpeed((s) => { const ns = s + 0.3; refs.current.speed = ns; return ns; });
      }

      if (!refs.current.over) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [started, gameOver, best]);

  const restart = () => {
    setLane(0); refs.current.lane = 0;
    setObstacles([]); setCoins([]); setScenery([]);
    setScore(0); refs.current.score = 0;
    setCoinCount(0); setDistance(0);
    setSpeed(2.5); refs.current.speed = 2.5;
    setGameOver(false); refs.current.over = false;
    refs.current.frame = 0;
    setStarted(true); setTilt(0); setFlash('');
  };

  // Touch
  const onTS = (e) => { touchRef.current = e.touches[0].clientX; };
  const onTE = (e) => {
    if (!touchRef.current) return;
    const dx = e.changedTouches[0].clientX - touchRef.current;
    if (Math.abs(dx) > 25) move(dx > 0 ? 1 : -1);
    touchRef.current = null;
  };

  // Perspective transform
  const zStyle = (z, laneIdx, isSide, side) => {
    const P = 80;
    const s = Math.max(0.1, P / (P + z));
    const y = 90 - s * 76;
    let x = isSide ? (side > 0 ? 50 + 40 * s : 50 - 40 * s) : LANE_X[laneIdx];
    if (!isSide) x = 50 + (x - 50) * s;
    return {
      left: `${x}%`, top: `${y}%`,
      transform: `translate(-50%, -50%) scale(${Math.max(s, 0.15)})`,
      zIndex: Math.round(900 - z),
      opacity: z > 120 ? Math.max(0, 1 - (z - 120) / 30) : 1,
    };
  };

  const spdPct = Math.min(100, (speed / 10) * 100);

  return (
    <div className="car-app">
      <div className="car-topbar">
        <div className="car-topbar-left">
          <button className="car-back-btn" onClick={() => navigate('/demo/games')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          </button>
          <h1 className="car-title">Road Racer</h1>
        </div>
        <div className="car-topbar-right">
          <div className="car-stat"><span className="car-stat-l">Score</span><span className="car-stat-v">{score}</span></div>
          <div className="car-stat"><span className="car-stat-l">Best</span><span className="car-stat-v">{best}</span></div>
          <div className="car-stat"><span className="car-stat-l">Coins</span><span className="car-stat-v">{coinCount}</span></div>
          <div className="car-stat"><span className="car-stat-l">Dist</span><span className="car-stat-v">{distance}m</span></div>
        </div>
      </div>

      <div className={`car-viewport ${flash}`} onTouchStart={onTS} onTouchEnd={onTE}>
        {/* Sky gradient — shifts with speed */}
        <div className="car-sky" style={{ background: `linear-gradient(180deg, #0c0c3a 0%, #1a1152 20%, #4a2068 40%, #d4556b 65%, #f4a460 85%, #ffd700 100%)` }}>
          <div className="car-stars">
            {Array.from({ length: 20 }).map((_, i) => (
              <span key={i} className="car-star" style={{ left: `${(i * 37 + 13) % 100}%`, top: `${(i * 23 + 7) % 35}%`, animationDelay: `${i * 0.3}s` }}></span>
            ))}
          </div>
          <div className="car-sun"></div>
          <div className="car-cloud c1">☁️</div>
          <div className="car-cloud c2">⛅</div>
        </div>

        {/* Horizon mountains */}
        <div className="car-horizon"></div>

        {/* Road area */}
        <div className={`car-road ${shakeRoad ? 'shake' : ''}`}>
          {/* Road surface */}
          <div className="car-road-bg">
            {/* Road edges */}
            <div className="car-edge left"></div>
            <div className="car-edge right"></div>

            {/* Lane dividers — animated */}
            <div className="car-divider d1" style={{ animationDuration: `${Math.max(0.15, 0.5 / (speed / 2.5))}s` }}></div>
            <div className="car-divider d2" style={{ animationDuration: `${Math.max(0.15, 0.5 / (speed / 2.5))}s` }}></div>
          </div>

          {/* Scenery */}
          {scenery.map((s) => (
            <div key={s.id} className="car-scenery-item" style={{ ...zStyle(s.z, 0, true, s.side), fontSize: '3rem' }}>{s.emoji}</div>
          ))}

          {/* Coins */}
          {coins.map((c) => (
            <div key={c.id} className="car-coin" style={{ ...zStyle(c.z, c.lane), fontSize: '2.5rem' }}>{COIN}</div>
          ))}

          {/* Obstacles */}
          {obstacles.map((o) => (
            <div key={o.id} className="car-enemy" style={{ ...zStyle(o.z, o.lane), fontSize: '3.2rem' }}>{o.emoji}</div>
          ))}

          {/* Player */}
          <div className={`car-player ${gameOver ? 'crash' : ''}`} style={{ left: `${LANE_X[lane]}%`, transform: `translateX(-50%) rotate(${tilt}deg)` }}>
            🚘
          </div>

          {/* Speed indicator */}
          <div className="car-speedo">
            <div className="car-speedo-fill" style={{ height: `${spdPct}%` }}></div>
            <span className="car-speedo-txt">{Math.round(speed * 30)} km/h</span>
          </div>
        </div>

        {/* Overlays */}
        {!started && !gameOver && (
          <div className="car-overlay">
            <div className="car-ov-box">
              <div className="car-ov-icon">🏎️</div>
              <h2>Road Racer</h2>
              <p>Dodge traffic, collect stars, survive as long as you can!</p>
              <div className="car-ov-controls">
                <div className="car-ov-ctrl"><kbd>←</kbd><kbd>→</kbd><span>or swipe</span></div>
              </div>
              <button className="car-go-btn" onClick={() => setStarted(true)}>
                <span>Start Racing</span>
                <span className="car-go-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {gameOver && (
          <div className="car-overlay crash-ov">
            <div className="car-ov-box">
              <div className="car-ov-icon">💥</div>
              <h2>Crashed!</h2>
              <div className="car-result-grid">
                <div className="car-result-item"><span>Score</span><strong>{score}</strong></div>
                <div className="car-result-item"><span>Distance</span><strong>{distance}m</strong></div>
                <div className="car-result-item"><span>Coins</span><strong>{coinCount}</strong></div>
                <div className="car-result-item"><span>Top Speed</span><strong>{Math.round(speed * 30)} km/h</strong></div>
              </div>
              {score >= best && score > 0 && <div className="car-new-best">🏆 New High Score!</div>}
              <button className="car-go-btn" onClick={restart}>
                <span>Play Again</span>
                <span className="car-go-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {/* Mobile controls */}
        <div className="car-mobile-ctrls">
          <button className="car-m-btn" onClick={() => move(-1)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button className="car-m-btn" onClick={() => move(1)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default CarGame;
